import { useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, configApi, UNAUTHORIZED_EVENT } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { useSessions } from '@/store/sessions';
import { useMyPrefsStore } from '@/lib/myPrefs';
import { useAgentConfigStore } from '@/lib/agentConfig';
import { Loader2 } from 'lucide-react';

/**
 * Boots the SPA's identity state. As of Phase 2.3 there is no login
 * screen — the server middleware auto-issues a client-id cookie on
 * first request — so this component's only jobs are:
 *
 *   1. Fire GET /api/auth/me on mount, store the resulting user in
 *      the auth store so the rest of the app can read it.
 *   2. Show a brief connecting-spinner while we wait.
 *   3. Refresh on `adh:unauthorized` (caused only by a wrong
 *      `?token=` / `x-adh-token` from a script — the SPA itself
 *      doesn't carry tokens). Refresh will mint a fresh client id
 *      via the middleware; no redirect needed.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  const setInitialised = useAuth((s) => s.setInitialised);
  const initialised = useAuth((s) => s.initialised);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['auth.me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });

  // Client-side config — currently just `ui.maxActiveSessions`. We
  // fire-and-forget: until the cap arrives the store uses its
  // DEFAULT_CAP=3 so the user can still open devices on a slow
  // network. Server overrides flow in seconds later.
  const setCap = useSessions((s) => s.setCap);
  const { data: cfg } = useQuery({
    queryKey: ['config.client'],
    queryFn: () => configApi.client(),
    retry: 1,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (cfg?.ui?.maxActiveSessions) setCap(cfg.ui.maxActiveSessions);
  }, [cfg, setCap]);

  useEffect(() => {
    if (data) {
      setUser(data.user);
      setInitialised(true);
    }
  }, [data, setUser, setInitialised]);

  // 阶段 A user prefs (B/B2) + agent config (C) — 一次性拉满。
  // 服务器侧 cookie 已 mint，identity 上线后这两个 store 自动 init。
  // beforeunload 强制 flush 防止用户调慢值后立刻关 tab 把变更扔了。
  useEffect(() => {
    if (!data?.user) return;
    void useMyPrefsStore.getState().init();
    void useAgentConfigStore.getState().init();
    const onUnload = (): void => {
      void useMyPrefsStore.getState().flush();
      void useAgentConfigStore.getState().flush();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [data?.user]);

  useEffect(() => {
    function onUnauth(): void {
      // 401 here is only reachable via a bad shared token — the SPA
      // itself runs cookie-only, so a re-fetch will mint a fresh
      // client id and recover transparently.
      setUser(null);
      qc.clear();
      void refetch();
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauth);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauth);
  }, [qc, setUser, refetch]);

  if (isLoading || (!initialised && isFetching)) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-bg text-ink-muted">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span className="text-sm">连接中…</span>
      </div>
    );
  }

  if (!data?.user) {
    // Should be unreachable after auto-issue rolls out, but if the
    // server somehow returns user: null (e.g. older backend), render
    // children anyway so the SPA isn't permanently blank.
    return <>{children}</>;
  }

  return <>{children}</>;
}
